import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Skeleton } from 'antd';
import { PiCameraBold } from 'react-icons/pi';
import { useAuthImageUrl } from '../hooks/useAuthImageUrl';

interface LazyImageProps {
  /** Direct public URL (no auth needed). Use this OR uuid, not both. */
  src?: string;
  /** FreePhoto UUID — image will be fetched with JWT auth via backend endpoint. */
  uuid?: string;
  /**
   * OSS-style resize param forwarded to the backend.
   * e.g. "image/resize,w_300" for thumbnails.
   * Only used when uuid is provided.
   */
  ossProcess?: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  onClick?: () => void;
}

/**
 * Lazy loading image component with Intersection Observer.
 * Supports two modes:
 * - `uuid` mode: fetches the image via authenticated axios call (JWT token injected),
 *   then renders via blob URL. Use for FreePhoto images.
 * - `src` mode: renders a plain <img> directly. Use for public images.
 */
export const LazyImage: React.FC<LazyImageProps> = ({ src, uuid, ossProcess, alt, width, height, className, onClick }) => {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  // Auth-fetch mode — only fires when in view and uuid provided
  const { blobUrl, loading: authLoading, error: authError } = useAuthImageUrl(
    uuid && isInView ? uuid : undefined,
    ossProcess ? { ossProcess } : undefined,
  );

  // Intersection observer for lazy loading
  useEffect(() => {
    if (!imgRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px',
      },
    );

    observer.observe(imgRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Reset loaded/error state when uuid or src changes
  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [uuid, src]);

  // Track auth errors
  useEffect(() => {
    if (authError) setHasError(true);
  }, [authError]);

  const handleLoad = () => setIsLoaded(true);
  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  // Determine actual src to render
  const resolvedSrc = uuid ? blobUrl : src;
  const isActuallyLoading = uuid ? (!isInView || authLoading) : !isInView;

  return (
    <UI_Wrapper ref={imgRef} className={className} style={{ width, height }} onClick={onClick}>
      {!isLoaded && !hasError && <Skeleton.Image active style={{ width: '100%', height: '100%' }} />}
      {resolvedSrc && !hasError && (
        <UI_Image src={resolvedSrc} alt={alt} onLoad={handleLoad} onError={handleError} $isLoaded={isLoaded} />
      )}
      {!resolvedSrc && !isActuallyLoading && !hasError && (
        // uuid mode: in view but blobUrl not yet ready — skeleton covers this
        null
      )}
      {hasError && (
        <UI_ErrorPlaceholder>
          <PiCameraBold style={{ fontSize: '24px', color: '#999' }} />
          <p>Không thể tải ảnh</p>
        </UI_ErrorPlaceholder>
      )}
    </UI_Wrapper>
  );
};

const UI_Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UI_Image = styled.img<{ $isLoaded: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${(props) => (props.$isLoaded ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

const UI_ErrorPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #f0f0f0;
  color: #999;

  span {
    font-size: 32px;
    margin-bottom: 8px;
  }

  p {
    font-size: 12px;
    margin: 0;
  }
`;